/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Controller } from 'react-hook-form';

import { Lista } from './Lista';
import { InputSelectProps } from './protocols';
import { InputContainer } from './styles';

export const InputSelect: React.FC<InputSelectProps> = ({
  label = '',
  ListaWidth = '100%',
  listaHeight = 'none',
  placeholder = '',
  name,
  isError,
  control,
  changeSelected,
  options,
  isDisabled = false,
  defaultOption,
  requiredSignal = false,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const inputId = `input-select-${name}`;
  const listId = `input-select-list-${name}`;

  const normalizedOptions = useMemo(() => {
    const safeOptions = options || [];

    if (defaultOption)
      return [{ label: 'NENHUM(A)', value: -1 }, ...safeOptions];

    return safeOptions;
  }, [defaultOption, options]);

  const handleDocumentKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (isOpen && event.key === 'Tab') setIsOpen(false);
    },
    [isOpen],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleDocumentKeyDown);

    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [handleDocumentKeyDown]);

  return (
    <InputContainer className={isOpen ? 'open' : ''}>
      <Form.Group controlId={inputId}>
        {label && (
          <Form.Label>{requiredSignal ? `${label} *` : label}</Form.Label>
        )}

        <Controller
          name={name}
          control={control}
          defaultValue={null}
          render={({ field }) => {
            const displayValue = field.value?.label ?? filter;

            const handleToggleList = () => {
              if (isDisabled) return;
              setIsOpen((prevState) => !prevState);
            };

            const handleInputChange = (
              event: React.ChangeEvent<HTMLInputElement>,
            ) => {
              const nextFilter = event.target.value.toUpperCase();

              setFilter(nextFilter);
              field.onChange(null);
              setIsOpen(true);
            };

            const handleInputBlur = () => {
              const selectedLabel = field.value?.label;

              if (!selectedLabel) return;

              const selectedOption = normalizedOptions.find(
                (option) => option.label === selectedLabel,
              );

              if (selectedOption) changeSelected?.(selectedOption);
            };

            const handleSelectOption = (option: any) => {
              field.onChange(option);
              setIsOpen(false);
              setFilter('');
              changeSelected?.(option);
            };

            return (
              <>
                <div className="select-container">
                  <input
                    {...rest}
                    id={inputId}
                    type="text"
                    disabled={isDisabled}
                    className={
                      isError ? 'form-control is-invalid' : 'form-control'
                    }
                    value={displayValue}
                    placeholder={placeholder}
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={isOpen}
                    aria-controls={listId}
                    aria-invalid={!!isError}
                    aria-autocomplete="list"
                    onClick={handleToggleList}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                  />

                  <div className="drop-indicator" aria-hidden="true">
                    <span role="img" aria-label="open">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        role="presentation"
                      >
                        <path
                          d="M8.292 10.293a1.009 1.009 0 000 1.419l2.939 2.965c.218.215.5.322.779.322s.556-.107.769-.322l2.93-2.955a1.01 1.01 0 000-1.419.987.987 0 00-1.406 0l-2.298 2.317-2.307-2.327a.99.99 0 00-1.406 0z"
                          fill="currentColor"
                          fillRule="evenodd"
                        />
                      </svg>
                    </span>
                  </div>
                </div>

                {isOpen && (
                  <div id={listId}>
                    <Lista
                      listaWidth={ListaWidth}
                      listaHeight={listaHeight}
                      options={normalizedOptions}
                      setIsOpen={setIsOpen}
                      filter={filter}
                      setFilter={setFilter}
                      changeSelected={handleSelectOption}
                      defaultOption={defaultOption}
                    />
                  </div>
                )}
              </>
            );
          }}
        />
      </Form.Group>
    </InputContainer>
  );
};
